import React, { useState, useEffect, useMemo, useId } from 'react';
import { Form, Button, Collapse, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
    FiFilter,
    FiChevronDown,
    FiChevronUp,
    FiSearch,
    FiX,
    FiInfo,
    FiRotateCcw,
    FiSliders,
    FiCheck,
} from 'react-icons/fi';
import {
    DEFAULT_FILTERS,
    DISTRIBUTION_TYPE_OPTIONS,
    OBSERVATION_SCOPE_OPTIONS,
    FORECAST_HOUR_OPTIONS,
} from '../utils/dashboardConstants';
import { formatReferenceDate } from '../utils/dateHelpers';
import { getAllPartners } from '../../../../services/api';
import { handleApiError } from '../../../../utils/errorHandler';
import PartnerCompanyAutocomplete from '../../../../components/Common/PartnerCompanyAutocomplete';

const ALL_FILTER_KEYS = [
    'run_date',
    'policy_code',
    'partner_id',
    'company_name',
    'reservation_number',
    'activity_name',
    'distribution_type',
    'include_completed',
    'forecast_timing_hour',
    'match_current_hour',
    'scope',
    'observation_timing_hour',
];

const PRIMARY_KEYS = ['run_date', 'policy_code', 'reservation_number', 'company_name'];
const SECONDARY_KEYS = [
    'activity_name',
    'distribution_type',
    'forecast_timing_hour',
    'observation_timing_hour',
    'scope',
    'include_completed',
    'match_current_hour',
];

/** Champ texte local, propagé au filtre (URL) après 300 ms sans frappe. */
const useDebouncedFilterField = (filters, key, onFilterChange) => {
    const urlValue = filters[key] || '';
    const [value, setValue] = useState(urlValue);

    useEffect(() => {
        setValue(urlValue);
    }, [urlValue]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (value !== urlValue) onFilterChange(key, value);
        }, 300);
        return () => clearTimeout(timer);
    }, [value, urlValue, key, onFilterChange]);

    return [value, setValue];
};

const labelFromOptions = (options, value) => options.find((opt) => String(opt.value) === String(value))?.label ?? value;

const SearchField = ({ id, label, value, onChange, placeholder }) => (
    <div className="ops-field">
        <label htmlFor={id} className="ops-field__label">{label}</label>
        <div className={`ops-input-group${value ? ' is-filled' : ''}`}>
            <FiSearch className="ops-input-group__icon" aria-hidden="true" />
            <input
                id={id}
                type="search"
                className="ops-input ops-input--with-icon"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoComplete="off"
            />
            {value && (
                <button
                    type="button"
                    className="ops-input-group__clear"
                    onClick={() => onChange('')}
                    aria-label={`Effacer ${label}`}
                >
                    <FiX aria-hidden="true" />
                </button>
            )}
        </div>
    </div>
);

const SelectField = ({ id, label, value, onChange, children, disabled, hint }) => (
    <div className="ops-field">
        <label htmlFor={id} className="ops-field__label">
            {label}
            {hint && <span className="ops-field__hint">{hint}</span>}
        </label>
        <select
            id={id}
            className={`ops-select${value !== '' && value !== null && value !== undefined ? ' is-filled' : ''}`}
            value={value ?? ''}
            onChange={onChange}
            disabled={disabled}
        >
            {children}
        </select>
    </div>
);

const SwitchField = ({ id, label, checked, onChange, tooltip }) => {
    const tooltipId = useId();
    return (
        <div className={`ops-switch${checked ? ' is-on' : ''}`}>
            <Form.Check
                type="switch"
                id={id}
                className="ops-switch__control"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                label={(
                    <span className="ops-switch__label">
                        {label}
                        <span className="ops-switch__state" aria-hidden="true">{checked ? 'Activé' : 'Désactivé'}</span>
                    </span>
                )}
            />
            {tooltip && (
                <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
                    <button type="button" className="ops-icon-btn ops-icon-btn--sm" aria-label={`Aide : ${label}. ${tooltip}`}>
                        <FiInfo aria-hidden="true" />
                    </button>
                </OverlayTrigger>
            )}
        </div>
    );
};

const DashboardFilters = ({
    filters,
    onFilterChange,
    onReset,
    hasActiveFilters,
    visibleFilters = ALL_FILTER_KEYS,
    showPartnerFilter = true,
    resultCount,
    resultLabel = 'résultat',
    onApply,
}) => {
    const applyMode = typeof onApply === 'function';
    const filtersKey = JSON.stringify(filters);
    const [draft, setDraft] = useState(filters);

    useEffect(() => {
        setDraft(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey]);

    const values = applyMode ? draft : filters;
    const setValue = (key, value) => {
        if (applyMode) setDraft((prev) => ({ ...prev, [key]: value }));
        else onFilterChange(key, value);
    };
    const isDirty = applyMode && ALL_FILTER_KEYS.some((key) => (draft[key] ?? '') !== (filters[key] ?? ''));

    const [localPolicyCode, setLocalPolicyCode] = useState(filters.policy_code);
    const [localCompanyName, setLocalCompanyName] = useState(filters.company_name);
    const [localReservation, setLocalReservation] = useDebouncedFilterField(filters, 'reservation_number', onFilterChange);
    const [localActivity, setLocalActivity] = useDebouncedFilterField(filters, 'activity_name', onFilterChange);
    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [partnersError, setPartnersError] = useState(null);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [showSecondary, setShowSecondary] = useState(true);
    const [resetFeedback, setResetFeedback] = useState(false);
    const panelId = useId();

    const isVisible = (key) => visibleFilters.includes(key)
        && (key !== 'partner_id' || showPartnerFilter);

    useEffect(() => {
        setLocalPolicyCode(filters.policy_code);
    }, [filters.policy_code]);

    useEffect(() => {
        setLocalCompanyName(filters.company_name);
    }, [filters.company_name]);

    const needsPartners = visibleFilters.includes('company_name') || visibleFilters.includes('partner_id');

    useEffect(() => {
        if (!needsPartners) return undefined;

        let cancelled = false;

        const loadPartners = async () => {
            setPartnersLoading(true);
            setPartnersError(null);
            try {
                const response = await getAllPartners();
                const list = Array.isArray(response?.users)
                    ? response.users
                    : (Array.isArray(response) ? response : []);
                if (!cancelled) setPartners(list);
            } catch (error) {
                if (!cancelled) {
                    setPartners([]);
                    setPartnersError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
                }
            } finally {
                if (!cancelled) setPartnersLoading(false);
            }
        };

        loadPartners();
        return () => { cancelled = true; };
    }, [needsPartners]);

    const partnerCompanyOptions = useMemo(() => {
        const names = partners
            .map((partner) => partner.company_name?.trim())
            .filter(Boolean);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [partners]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (localPolicyCode !== filters.policy_code) {
                onFilterChange('policy_code', localPolicyCode);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [localPolicyCode, filters.policy_code, onFilterChange]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (localCompanyName !== filters.company_name) {
                onFilterChange('company_name', localCompanyName);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [localCompanyName, filters.company_name, onFilterChange]);

    useEffect(() => {
        if (!resetFeedback) return undefined;
        const timer = setTimeout(() => setResetFeedback(false), 2500);
        return () => clearTimeout(timer);
    }, [resetFeedback]);

    const partnerName = (id) => {
        const partner = partners.find((p) => String(p.partner_id || p.id) === String(id));
        return partner?.company_name || partner?.name || id;
    };

    const activeChips = useMemo(() => {
        const chips = [];
        const push = (key, label, value) => {
            if (isVisible(key)) chips.push({ key, label, value });
        };

        if (filters.run_date) push('run_date', 'Date', formatReferenceDate(filters.run_date));
        if (filters.policy_code) push('policy_code', 'Code police', filters.policy_code);
        if (filters.reservation_number) push('reservation_number', 'Réservation', filters.reservation_number);
        if (filters.company_name) push('company_name', 'Partenaire', filters.company_name);
        if (filters.activity_name) push('activity_name', 'Activité', filters.activity_name);
        if (filters.partner_id && visibleFilters.includes('partner_id')) {
            chips.push({ key: 'partner_id', label: 'Partenaire', value: partnerName(filters.partner_id) });
        }
        if (filters.distribution_type) {
            push('distribution_type', 'Distribution', labelFromOptions(DISTRIBUTION_TYPE_OPTIONS, filters.distribution_type));
        }
        if (filters.forecast_timing_hour !== null && filters.forecast_timing_hour !== undefined) {
            push('forecast_timing_hour', 'Heure forecast', `${filters.forecast_timing_hour}h`);
        }
        if (filters.observation_timing_hour !== null && filters.observation_timing_hour !== undefined) {
            push('observation_timing_hour', 'Heure observation', `${filters.observation_timing_hour}h`);
        }
        if (filters.scope && filters.scope !== DEFAULT_FILTERS.scope) {
            push('scope', 'Périmètre', labelFromOptions(OBSERVATION_SCOPE_OPTIONS, filters.scope));
        }
        if (filters.include_completed === false) push('include_completed', 'Checks terminés', 'exclus');
        if (filters.match_current_hour) push('match_current_hour', 'Heure actuelle Paris', 'activée');
        return chips;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, partners, visibleFilters, showPartnerFilter]);

    const clearChip = (key) => {
        if (key === 'policy_code') setLocalPolicyCode('');
        if (key === 'company_name') setLocalCompanyName('');
        if (key === 'reservation_number') setLocalReservation('');
        if (key === 'activity_name') setLocalActivity('');
        onFilterChange(key, DEFAULT_FILTERS[key] ?? '');
    };

    const handleReset = () => {
        setLocalPolicyCode('');
        setLocalCompanyName('');
        setLocalReservation('');
        setLocalActivity('');
        if (applyMode) setDraft({ ...filters, ...DEFAULT_FILTERS });
        onReset();
        setResetFeedback(true);
    };

    const handleApply = (event) => {
        event?.preventDefault();
        if (!applyMode) return;
        const payload = ALL_FILTER_KEYS.reduce((acc, key) => {
            if (isVisible(key)) acc[key] = draft[key];
            return acc;
        }, {});
        onApply(payload);
    };

    const textField = (key, localValue, setLocalValue) => (applyMode
        ? { value: draft[key] || '', onChange: (v) => setValue(key, v) }
        : { value: localValue, onChange: setLocalValue });

    const primaryVisible = PRIMARY_KEYS.filter(isVisible);
    const secondaryVisible = SECONDARY_KEYS.filter(isVisible);
    const secondaryActiveCount = activeChips.filter((chip) => SECONDARY_KEYS.includes(chip.key)).length;
    const canReset = hasActiveFilters || activeChips.length > 0 || isDirty;
    const policyCodeField = textField('policy_code', localPolicyCode, setLocalPolicyCode);
    const reservationField = textField('reservation_number', localReservation, setLocalReservation);
    const companyNameField = textField('company_name', localCompanyName, setLocalCompanyName);
    const activityField = textField('activity_name', localActivity, setLocalActivity);

    return (
        <form className="ops-filters" aria-label="Filtres de recherche" onSubmit={handleApply} noValidate>
            <div className="ops-filters__bar">
                <button
                    type="button"
                    className="ops-filters__toggle"
                    onClick={() => setFiltersOpen((prev) => !prev)}
                    aria-expanded={filtersOpen}
                    aria-controls={panelId}
                >
                    <FiFilter aria-hidden="true" />
                    <span className="ops-filters__title">Filtres</span>
                    {activeChips.length > 0 && (
                        <span className="ops-count-pill" aria-label={`${activeChips.length} filtre(s) actif(s)`}>
                            {activeChips.length}
                        </span>
                    )}
                    {filtersOpen ? <FiChevronUp aria-hidden="true" /> : <FiChevronDown aria-hidden="true" />}
                </button>

                {typeof resultCount === 'number' && (
                    <span className="ops-filters__results" aria-live="polite">
                        <strong>{resultCount.toLocaleString('fr-FR')}</strong>
                        {' '}
                        {resultLabel}{resultCount > 1 ? 's' : ''}
                        {activeChips.length > 0 && <span className="ops-muted"> · filtré{resultCount > 1 ? 's' : ''}</span>}
                    </span>
                )}

                <div className="ops-filters__actions">
                    <span className="ops-filters__feedback" aria-live="polite">
                        {resetFeedback && (
                            <>
                                <FiCheck aria-hidden="true" /> Filtres réinitialisés
                            </>
                        )}
                        {!resetFeedback && isDirty && (
                            <span className="ops-filters__pending">Modifications non appliquées</span>
                        )}
                    </span>
                    {secondaryVisible.length > 0 && filtersOpen && (
                        <Button
                            variant="link"
                            className="ops-btn ops-btn--ghost"
                            onClick={() => setShowSecondary((prev) => !prev)}
                            aria-expanded={showSecondary}
                        >
                            <FiSliders aria-hidden="true" />
                            {showSecondary ? 'Moins de filtres' : 'Plus de filtres'}
                            {!showSecondary && secondaryActiveCount > 0 && (
                                <span className="ops-count-pill ops-count-pill--sm">{secondaryActiveCount}</span>
                            )}
                        </Button>
                    )}
                    <Button
                        variant="outline-secondary"
                        className="ops-btn ops-btn--secondary"
                        onClick={handleReset}
                        disabled={!canReset}
                    >
                        <FiRotateCcw aria-hidden="true" />
                        Réinitialiser
                    </Button>
                    {applyMode && (
                        <Button
                            type="submit"
                            variant="primary"
                            className={`ops-btn ops-btn--primary${isDirty ? ' is-pending' : ''}`}
                        >
                            <FiFilter aria-hidden="true" />
                            Filtrer
                        </Button>
                    )}
                </div>
            </div>

            <Collapse in={filtersOpen}>
                <div id={panelId}>
                    <div className="ops-filters__body">
                        {primaryVisible.length > 0 && (
                            <div className="ops-filters__grid ops-filters__grid--primary">
                                {isVisible('run_date') && (
                                    <div className="ops-field">
                                        <label htmlFor="ops-filter-run-date" className="ops-field__label">Date</label>
                                        <input
                                            id="ops-filter-run-date"
                                            type="date"
                                            className={`ops-input${values.run_date ? ' is-filled' : ''}`}
                                            value={values.run_date || ''}
                                            onChange={(e) => setValue('run_date', e.target.value || null)}
                                        />
                                    </div>
                                )}

                                {isVisible('policy_code') && (
                                    <SearchField
                                        id="ops-filter-policy-code"
                                        label="Code police"
                                        value={policyCodeField.value}
                                        onChange={policyCodeField.onChange}
                                        placeholder="Ex. 236079703555"
                                    />
                                )}

                                {isVisible('reservation_number') && (
                                    <SearchField
                                        id="ops-filter-reservation"
                                        label="Réservation"
                                        value={reservationField.value}
                                        onChange={reservationField.onChange}
                                        placeholder="N° de réservation"
                                    />
                                )}

                                {isVisible('company_name') && (
                                    <div className={`ops-field ops-field--partner${companyNameField.value ? ' is-filled' : ''}`}>
                                        <span className="ops-field__label" id="ops-filter-company-name-label">Partenaire</span>
                                        <div aria-labelledby="ops-filter-company-name-label">
                                            <PartnerCompanyAutocomplete
                                                value={companyNameField.value}
                                                onChange={companyNameField.onChange}
                                                options={partnerCompanyOptions}
                                                loading={partnersLoading}
                                                error={partnersError}
                                                placeholder="Rechercher un partenaire…"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {secondaryVisible.length > 0 && (
                            <Collapse in={showSecondary}>
                                <div>
                                    <div className="ops-filters__grid ops-filters__grid--secondary">
                                        {isVisible('activity_name') && (
                                            <SearchField
                                                id="ops-filter-activity"
                                                label="Activité"
                                                value={activityField.value}
                                                onChange={activityField.onChange}
                                                placeholder="Nom de l’activité"
                                            />
                                        )}

                                        {isVisible('distribution_type') && (
                                            <SelectField
                                                id="ops-filter-distribution"
                                                label="Distribution"
                                                value={values.distribution_type}
                                                onChange={(e) => setValue('distribution_type', e.target.value)}
                                            >
                                                {DISTRIBUTION_TYPE_OPTIONS.map((opt) => (
                                                    <option key={opt.value || 'all'} value={opt.value}>
                                                        {opt.value === '' ? 'Toutes les distributions' : opt.label}
                                                    </option>
                                                ))}
                                            </SelectField>
                                        )}

                                        {isVisible('forecast_timing_hour') && (
                                            <SelectField
                                                id="ops-filter-forecast-hour"
                                                label="Heure forecast"
                                                hint={values.match_current_hour ? 'Heure Paris active' : null}
                                                value={values.forecast_timing_hour ?? ''}
                                                disabled={values.match_current_hour}
                                                onChange={(e) => setValue(
                                                    'forecast_timing_hour',
                                                    e.target.value === '' ? null : Number(e.target.value)
                                                )}
                                            >
                                                <option value="">Toutes les heures</option>
                                                {FORECAST_HOUR_OPTIONS.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </SelectField>
                                        )}

                                        {isVisible('observation_timing_hour') && (
                                            <SelectField
                                                id="ops-filter-observation-hour"
                                                label="Heure observation"
                                                value={values.observation_timing_hour ?? ''}
                                                onChange={(e) => setValue(
                                                    'observation_timing_hour',
                                                    e.target.value === '' ? null : Number(e.target.value)
                                                )}
                                            >
                                                <option value="">Toutes les heures</option>
                                                {FORECAST_HOUR_OPTIONS.map((opt) => (
                                                    <option key={`obs-${opt.value}`} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </SelectField>
                                        )}

                                        {isVisible('scope') && (
                                            <SelectField
                                                id="ops-filter-scope"
                                                label="Périmètre observation"
                                                value={values.scope === DEFAULT_FILTERS.scope ? '' : values.scope}
                                                onChange={(e) => setValue('scope', e.target.value || DEFAULT_FILTERS.scope)}
                                            >
                                                {OBSERVATION_SCOPE_OPTIONS.map((opt) => (
                                                    <option key={opt.value} value={opt.value === DEFAULT_FILTERS.scope ? '' : opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </SelectField>
                                        )}

                                        {(isVisible('include_completed') || isVisible('match_current_hour')) && (
                                            <div className="ops-filters__switches">
                                                {isVisible('include_completed') && (
                                                    <SwitchField
                                                        id="include-completed"
                                                        label="Inclure les checks terminés"
                                                        checked={values.include_completed}
                                                        onChange={(value) => setValue('include_completed', value)}
                                                        tooltip="Affiche aussi les policies dont le check a déjà été effectué."
                                                    />
                                                )}
                                                {isVisible('match_current_hour') && (
                                                    <SwitchField
                                                        id="match-current-hour"
                                                        label="Heure actuelle Paris"
                                                        checked={values.match_current_hour}
                                                        onChange={(value) => setValue('match_current_hour', value)}
                                                        tooltip="Limite aux forecasts dont l’heure de check correspond à l’heure actuelle à Paris. Remplace le filtre « Heure forecast »."
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Collapse>
                        )}
                    </div>
                </div>
            </Collapse>

            {activeChips.length > 0 && (
                <div className="ops-filters__chips">
                    <span className="ops-filters__chips-label">Filtres actifs :</span>
                    <ul className="ops-chip-list">
                        {activeChips.map((chip) => (
                            <li key={chip.key}>
                                <span className="ops-chip">
                                    <span className="ops-chip__label">{chip.label} :</span>
                                    <span className="ops-chip__value">{chip.value}</span>
                                    <button
                                        type="button"
                                        className="ops-chip__remove"
                                        onClick={() => clearChip(chip.key)}
                                        aria-label={`Retirer le filtre ${chip.label}`}
                                    >
                                        <FiX aria-hidden="true" />
                                    </button>
                                </span>
                            </li>
                        ))}
                    </ul>
                    <button type="button" className="ops-link-btn" onClick={handleReset}>
                        Tout effacer
                    </button>
                </div>
            )}
        </form>
    );
};

export default DashboardFilters;
